import React, { useState, useEffect, useCallback } from 'react';
import { X, Link as LinkIcon, Copy, Check } from 'react-feather';
import AuthService from '../../services/authService';

/**
 * Tracked Links — campaign links with no agent attached.
 *
 * A signup through one of these is attributed to the campaign, but the user
 * enters with no agent and is stamped with poolExcludedAt, so they never
 * appear in Available Leads and can't be claimed. An admin can still assign
 * them an agent by hand afterwards, and the user can still enter an agent's
 * referral code themselves.
 *
 * Deliberately separate from the per-agent Referral Links modal: these links
 * belong to no agent, so there's no agent row to hang them off.
 */
const API_URL = 'https://back-end-x7tx.onrender.com/api';

const TrackedLinksModal = ({ onClose }) => {
  const [links, setLinks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copiedCode, setCopiedCode] = useState(null);
  const [form, setForm] = useState({ campaignName: '', notes: '' });

  const fetchLinks = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/admin/referrals?type=tracking`, {
        headers: { Authorization: `Bearer ${AuthService.getToken()}` }
      });
      if (!response.ok) throw new Error('Failed to load tracked links');
      const data = await response.json();
      setLinks(Array.isArray(data) ? data : []);
      setError('');
    } catch (err) {
      console.error('Error fetching tracked links:', err);
      setError('Could not load tracked links');
      setLinks([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.campaignName.trim()) return;

    setIsCreating(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/admin/referrals/tracking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({
          campaignName: form.campaignName.trim(),
          notes: form.notes.trim()
        })
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to create tracked link');
      }

      setSuccess(`Created ${data.referralLink.linkCode}`);
      setForm({ campaignName: '', notes: '' });
      await fetchLinks();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsCreating(false);
    }
  };

  const copy = async (url, code) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 1600);
    } catch (err) {
      console.error('Clipboard write failed:', err);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-content-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <LinkIcon size={16} />
            Tracked Links
          </h3>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          <div style={{
            background: '#eff6ff',
            border: '1px solid #dbeafe',
            borderRadius: '8px',
            padding: '12px 14px',
            marginBottom: '20px',
            fontSize: '0.8rem',
            color: '#1e40af',
            lineHeight: 1.5
          }}>
            Signups through these links are attributed to the campaign but enter with
            no agent and never join the claimable pool. You can still assign an agent
            manually afterwards.
          </div>

          {/* Create */}
          <div style={{
            background: '#fafafa',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px',
            border: '1px solid #f0f0f0'
          }}>
            <h4 style={{ margin: '0 0 16px 0', fontSize: '0.95rem', fontWeight: '600', color: '#111' }}>
              Create Tracked Link
            </h4>
            {error && <div className="alert-danger">{error}</div>}
            {success && <div className="alert-success">{success}</div>}
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label className="form-label">Campaign Name</label>
                <input
                  type="text"
                  value={form.campaignName}
                  onChange={(e) => setForm({ ...form, campaignName: e.target.value })}
                  className="form-control"
                  placeholder="e.g. Tracked Link - 1"
                  required
                />
                <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '4px' }}>
                  The only label distinguishing this link in reporting — make it specific.
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes (optional)</label>
                <input
                  type="text"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="form-control"
                  placeholder="Internal notes"
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={isCreating}>
                {isCreating ? 'Creating...' : 'Create Link'}
              </button>
            </form>
          </div>

          {/* List */}
          <h4 style={{ margin: '0 0 12px 0', fontSize: '0.95rem', fontWeight: '600', color: '#111' }}>
            Existing Links ({links.length})
          </h4>

          {isLoading ? (
            <div style={{
              textAlign: 'center', padding: '24px', background: '#fafafa',
              borderRadius: '12px', color: '#9ca3af', border: '1px solid #f0f0f0'
            }}>
              Loading...
            </div>
          ) : links.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '24px', background: '#fafafa',
              borderRadius: '12px', color: '#9ca3af', border: '1px solid #f0f0f0'
            }}>
              No tracked links yet
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {links.map(link => (
                <div key={link.id} style={{
                  background: '#fafafa',
                  borderRadius: '8px',
                  padding: '14px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px',
                  border: '1px solid #f0f0f0'
                }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: '500', color: '#111', marginBottom: '4px' }}>
                      {link.campaignName}
                    </div>
                    <code style={{ fontSize: '0.8rem' }}>{link.linkCode}</code>
                    {link.adminNotes && (
                      <div style={{
                        fontSize: '0.72rem', color: '#9ca3af', marginTop: '4px',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                      }}>
                        {link.adminNotes}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0 }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: '600', color: '#111' }}>{link.stats?.clicks || 0}</div>
                      <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>Clicks</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: '600', color: '#111' }}>{link.stats?.installs || 0}</div>
                      <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>Installs</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: '600', color: '#111' }}>{link.stats?.registrations || 0}</div>
                      <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>Signups</div>
                    </div>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => copy(link.shareableUrl, link.linkCode)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                    >
                      {copiedCode === link.linkCode ? <Check size={13} /> : <Copy size={13} />}
                      {copiedCode === link.linkCode ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default TrackedLinksModal;